import React, { useEffect, useState, useRef, Children, cloneElement, isValidElement } from 'react'
import { cn } from '@/lib/utils'

// ============================================================
// REDUCED MOTION HOOK - Respects user preferences
// ============================================================

export function useReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches)
    }

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
    // Legacy browsers
    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [])

  return prefersReducedMotion
}

// ============================================================
// ANIMATED LIST - Staggered list item animations
// ============================================================

interface AnimatedListProps {
  children: React.ReactNode
  className?: string
  staggerDelay?: number // ms between each item
  initialDelay?: number // ms before first item
  animation?: 'fade-up' | 'fade-left' | 'fade-right' | 'scale' | 'fade'
  duration?: number // ms
}

export function AnimatedList({
  children,
  className,
  staggerDelay = 50,
  initialDelay = 0,
  animation = 'fade-up',
  duration = 300
}: AnimatedListProps) {
  const [isVisible, setIsVisible] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Use Intersection Observer for visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const animationClasses = {
    'fade-up': {
      initial: 'opacity-0 translate-y-4',
      animate: 'opacity-100 translate-y-0'
    },
    'fade-left': {
      initial: 'opacity-0 translate-x-4',
      animate: 'opacity-100 translate-x-0'
    },
    'fade-right': {
      initial: 'opacity-0 -translate-x-4',
      animate: 'opacity-100 translate-x-0'
    },
    'scale': {
      initial: 'opacity-0 scale-95',
      animate: 'opacity-100 scale-100'
    },
    'fade': {
      initial: 'opacity-0',
      animate: 'opacity-100'
    }
  }

  const classes = animationClasses[animation]

  return (
    <div ref={containerRef} className={className}>
      {Children.map(children, (child, index) => {
        if (!isValidElement(child)) return child

        const delay = initialDelay + index * staggerDelay

        return cloneElement(child as React.ReactElement<{ className?: string; style?: React.CSSProperties }>, {
          className: cn(
            (child.props as { className?: string }).className,
            'transition-all ease-out',
            isVisible ? classes.animate : classes.initial
          ),
          style: {
            ...(child.props as { style?: React.CSSProperties }).style,
            transitionDuration: `${duration}ms`,
            transitionDelay: `${delay}ms`
          }
        })
      })}
    </div>
  )
}

// ============================================================
// PAGE TRANSITION - Wrapper for page-level transitions
// ============================================================

interface PageTransitionProps {
  children: React.ReactNode
  className?: string
  animation?: 'fade' | 'slide-up' | 'slide-left' | 'zoom'
  duration?: number
}

export function PageTransition({
  children,
  className,
  animation = 'fade',
  duration = 300
}: PageTransitionProps) {
  const [isVisible, setIsVisible] = useState(false)
  const prefersReducedMotion = useReducedMotion()

  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => setIsVisible(true), 10)
    return () => clearTimeout(timer)
  }, [])

  // If user prefers reduced motion, render children directly
  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>
  }

  const animationClasses = {
    'fade': {
      initial: 'opacity-0',
      animate: 'opacity-100'
    },
    'slide-up': {
      initial: 'opacity-0 translate-y-8',
      animate: 'opacity-100 translate-y-0'
    },
    'slide-left': {
      initial: 'opacity-0 translate-x-8',
      animate: 'opacity-100 translate-x-0'
    },
    'zoom': {
      initial: 'opacity-0 scale-95',
      animate: 'opacity-100 scale-100'
    }
  }

  const classes = animationClasses[animation]

  return (
    <div
      className={cn(
        'transition-all ease-out',
        isVisible ? classes.animate : classes.initial,
        className
      )}
      style={{ transitionDuration: `${duration}ms` }}
    >
      {children}
    </div>
  )
}

// ============================================================
// FADE IN - Simple fade-in wrapper
// ============================================================

interface FadeInProps {
  children: React.ReactNode
  className?: string
  delay?: number
  duration?: number
  direction?: 'up' | 'down' | 'left' | 'right' | 'none'
  distance?: number
}

export function FadeIn({
  children,
  className,
  delay = 0,
  duration = 300,
  direction = 'up',
  distance = 16
}: FadeInProps) {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [])

  const getTransform = () => {
    if (!isVisible) {
      switch (direction) {
        case 'up': return `translateY(${distance}px)`
        case 'down': return `translateY(-${distance}px)`
        case 'left': return `translateX(${distance}px)`
        case 'right': return `translateX(-${distance}px)`
        default: return 'none'
      }
    }
    return 'none'
  }

  return (
    <div
      ref={ref}
      className={cn('transition-all ease-out', className)}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: getTransform(),
        transitionDuration: `${duration}ms`,
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  )
}

// ============================================================
// SCALE IN - Scale animation wrapper
// ============================================================

interface ScaleInProps {
  children: React.ReactNode
  className?: string
  delay?: number
  duration?: number
  scale?: number
}

export function ScaleIn({
  children,
  className,
  delay = 0,
  duration = 300,
  scale = 0.95
}: ScaleInProps) {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={cn('transition-all ease-out', className)}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'scale(1)' : `scale(${scale})`,
        transitionDuration: `${duration}ms`,
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  )
}

// ============================================================
// HOVER CARD - Card with hover lift effect
// ============================================================

interface HoverCardProps {
  children: React.ReactNode
  className?: string
  hoverScale?: number
  hoverLift?: number
  glowColor?: string
}

export function HoverCard({
  children,
  className,
  hoverScale = 1.02,
  hoverLift = 4,
  glowColor = 'rgba(110, 64, 255, 0.1)'
}: HoverCardProps) {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        'transition-all duration-300 ease-out',
        className
      )}
      style={{
        transform: isHovered ? `scale(${hoverScale}) translateY(-${hoverLift}px)` : 'none',
        boxShadow: isHovered ? `0 ${hoverLift * 4}px ${hoverLift * 8}px ${glowColor}` : 'none'
      }}
    >
      {children}
    </div>
  )
}

// ============================================================
// REVEAL - Reveal on scroll with clip-path
// ============================================================

interface RevealProps {
  children: React.ReactNode
  className?: string
  direction?: 'up' | 'down' | 'left' | 'right'
  duration?: number
  delay?: number
}

export function Reveal({
  children,
  className,
  direction = 'up',
  duration = 500,
  delay = 0
}: RevealProps) {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [])

  const getClipPath = () => {
    if (isVisible) return 'inset(0 0 0 0)'

    switch (direction) {
      case 'up': return 'inset(100% 0 0 0)'
      case 'down': return 'inset(0 0 100% 0)'
      case 'left': return 'inset(0 100% 0 0)'
      case 'right': return 'inset(0 0 0 100%)'
      default: return 'inset(100% 0 0 0)'
    }
  }

  return (
    <div
      ref={ref}
      className={cn('transition-all ease-out overflow-hidden', className)}
      style={{
        clipPath: getClipPath(),
        transitionDuration: `${duration}ms`,
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  )
}

// ============================================================
// TYPEWRITER - Text typewriter effect
// ============================================================

interface TypewriterProps {
  text: string
  className?: string
  speed?: number // ms per character
  delay?: number // initial delay
  cursor?: boolean
}

export function Typewriter({
  text,
  className,
  speed = 50,
  delay = 0,
  cursor = true
}: TypewriterProps) {
  const [displayedText, setDisplayedText] = useState('')
  const [isComplete, setIsComplete] = useState(false)

  useEffect(() => {
    let index = 0
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        if (index < text.length) {
          setDisplayedText(text.slice(0, index + 1))
          index++
        } else {
          setIsComplete(true)
          clearInterval(interval)
        }
      }, speed)

      return () => clearInterval(interval)
    }, delay)

    return () => clearTimeout(timer)
  }, [text, speed, delay])

  return (
    <span className={className}>
      {displayedText}
      {cursor && !isComplete && (
        <span className="animate-pulse">|</span>
      )}
    </span>
  )
}

// ============================================================
// PULSE DOT - Animated pulse indicator
// ============================================================

interface PulseDotProps {
  color?: string
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export function PulseDot({
  color = 'bg-emerald-500',
  size = 'md',
  className
}: PulseDotProps) {
  const sizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
  }

  return (
    <span className={cn('relative flex', sizeClasses[size], className)}>
      <span
        className={cn(
          'animate-ping absolute inline-flex h-full w-full rounded-full opacity-75',
          color
        )}
      />
      <span
        className={cn(
          'relative inline-flex rounded-full h-full w-full',
          color
        )}
      />
    </span>
  )
}

// ============================================================
// SHIMMER - Loading shimmer effect
// ============================================================

interface ShimmerProps {
  className?: string
  width?: string | number
  height?: string | number
}

export function Shimmer({
  className,
  width = '100%',
  height = 16
}: ShimmerProps) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded bg-white/5',
        className
      )}
      style={{ width, height }}
    >
      <div
        className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"
        style={{ animationDuration: '1.5s' }}
      />
    </div>
  )
}
